import { Component, ErrorHandler, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { ContaParcelaService } from "../shared/conta-parcela.service";
import { ConfirmDialogComponent } from "src/app/core/dialogs/confirm-dialog/confirm-dialog.component";
import { InfoDialogComponent } from "src/app/core/dialogs/info-dialog/info-dialog.component";
import { AcaoNotaFiscalSubstituicao } from "../shared/nota-fiscal-conta-pagar.model";
import { ContaParcelaSubstituicaoNotaFiscalComponent } from "../conta-parcela-substituicao-nota-fiscal/conta-parcela-substituicao-nota-fiscal.component";

export type TipoBusca = 'ID' | 'PARCEIRO' | 'DESCRICAO';

export interface ContaParcelaMapeamento {
  contaParcelaId: number;
  descricao: string;
  dataVencimento: Date;
  valorParcela: number;
  jaSelecionada?: boolean;
}

export interface ContaParcelaSubstituicaoMapearData {
  contaParcelaId: number;          // conta parcela que está sendo substituída
  tpConta: number;
  possuiNotaFiscalAtiva: boolean;  // a substituição pergunta o que fazer com a nota
}

@Component({
  selector: 'conta-parcela-substituicao-mapear',
  templateUrl: 'conta-parcela-substituicao-mapear.component.html',
  styleUrls: ['conta-parcela-substituicao-mapear.component.css']
})
export class ContaParcelaSubstituicaoMapearComponent implements OnInit, OnDestroy {

  private readonly TAMANHO_PAGINA = 50;
  private readonly MIN_CARACTERES = 3;
  private destroy$ = new Subject<void>();

  // cabeçalho
  textoTpContaView: string;
  maximizado = false;

  // busca
  tipoBusca = new FormControl('ID');
  idBusca = new FormControl('');
  termoBusca = new FormControl('');
  resultadoPorId: ContaParcelaMapeamento | null = null;
  buscouPorId = false;

  // autocomplete
  opcoes: ContaParcelaMapeamento[] = [];
  temMais = false;
  carregando = false;
  private termoAtual = '';

  // contas parcelas escolhidas para a substituição
  selecionadas: ContaParcelaMapeamento[] = [];

  constructor(
    private dialogRef: MatDialogRef<ContaParcelaSubstituicaoMapearComponent>,
    @Inject(MAT_DIALOG_DATA) public IContaParcelaData: ContaParcelaSubstituicaoMapearData,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private contaParcelaService: ContaParcelaService,
    private errorHandler: ErrorHandler
  ) { }

  get ehContaAReceber(): boolean {
    return this.IContaParcelaData.tpConta === 0;
  }

  ngOnInit(): void {
    this.tipoBusca.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.trocarTipoBusca());

    this.textoTpContaView = this.ehContaAReceber ? 'Conta a Receber' : 'Conta a Pagar';

    this.termoBusca.valueChanges.pipe(
      debounceTime(300),
      filter(valor => typeof valor === 'string'),
      distinctUntilChanged(),
      tap(valor => {
        this.termoAtual = valor;
        this.opcoes = [];
        this.temMais = false;
      }),
      filter(valor => valor.length >= this.MIN_CARACTERES),
      tap(() => this.carregando = true),
      switchMap(valor => this.buscarPagina(valor, 0).pipe(
        catchError(err => {
          this.errorHandler.handleError(err);
          return of([]);
        })
      )),
      takeUntil(this.destroy$)
    ).subscribe(itens => {
      this.opcoes = this.marcarSelecionadas(itens);
      this.temMais = itens.length === this.TAMANHO_PAGINA;
      this.carregando = false;
    });

    this.restaurarDialog();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- cabeçalho ----------
  maximizarDialog(): void {
    this.maximizado = true;
    this.dialogRef.updateSize('100vw', '100vh');
  }

  restaurarDialog(): void {
    this.maximizado = false;
    this.dialogRef.updateSize('60%', '');
  }

  fecharDialog(substituiu: boolean = false): void {
    this.dialogRef.close(substituiu);
  }

  // ---------- tipo de busca ----------
  trocarTipoBusca(): void {
    // limpa apenas o estado da busca; a lista de selecionadas é mantida
    this.idBusca.setValue('');
    this.termoBusca.setValue('');
    this.termoAtual = '';
    this.resultadoPorId = null;
    this.buscouPorId = false;
    this.opcoes = [];
    this.temMais = false;
  }

  // ---------- buscar por ID ----------
  buscarPorId(): void {
    const buscaParcelaId = Number(this.idBusca.value);
    if (!buscaParcelaId) {
      return;
    }

    this.buscouPorId = false;
    this.contaParcelaService.buscarParaSubstituicaoPorId(
      buscaParcelaId,
      this.IContaParcelaData.tpConta,
      this.IContaParcelaData.contaParcelaId
    )
      .subscribe(data => {
        const body = data.body;
        this.resultadoPorId = body && body.contaParcelaId
          ? this.marcarSelecionadas([body])[0]
          : null;
        this.buscouPorId = true;
      });
  }

  // ---------- buscar por parceiro / descrição (autocomplete) ----------
  private buscarPagina(termo: string, skip: number): Observable<any[]> {
    if (this.tipoBusca.value === 'PARCEIRO') {
      return this.contaParcelaService.buscarParaSubstituicaoPorParceiro(
        termo, skip, this.TAMANHO_PAGINA, this.IContaParcelaData.tpConta, this.IContaParcelaData.contaParcelaId)
        .pipe(
          map(response => response.body ?? [])
        );
    }
    if (this.tipoBusca.value === 'DESCRICAO') {
      return this.contaParcelaService.buscarParaSubstituicaoPorDescricao(
        termo, skip, this.TAMANHO_PAGINA, this.IContaParcelaData.tpConta, this.IContaParcelaData.contaParcelaId)
        .pipe(
          map(response => response.body ?? [])
        );
    }
    return of([]);
  }

  verMais(event: Event): void {
    event.stopPropagation(); // mantém o painel do autocomplete aberto
    if (this.carregando) {
      return;
    }
    this.carregando = true;
    this.buscarPagina(this.termoAtual, this.opcoes.length)
      .pipe(finalize(() => this.carregando = false))
      .subscribe(itens => {
        this.opcoes = [...this.opcoes, ...this.marcarSelecionadas(itens)];
        this.temMais = itens.length === this.TAMANHO_PAGINA;
      });
  }

  aoSelecionarOpcao(conta: ContaParcelaMapeamento): void {

    if (!conta) {
      // seleção do "Ver mais": o mat-autocomplete já apagou o texto do campo
      this.termoBusca.setValue(this.termoAtual);
      return;
    }

    this.adicionarSelecionada(conta);
    this.termoBusca.setValue('');
    this.termoAtual = '';
    this.opcoes = [];
    this.temMais = false;
  }

  // ---------- lista de selecionadas ----------
  adicionarSelecionada(conta: ContaParcelaMapeamento): void {
    if (!conta || this.jaSelecionada(conta)) {
      return;
    }
    this.selecionadas = [...this.selecionadas, conta];
  }

  selecionarResultadoPorId(): void {
    this.adicionarSelecionada(this.resultadoPorId);
    this.resultadoPorId = null;
    this.buscouPorId = false;
    this.idBusca.setValue('');
  }

  removerSelecionada(conta: ContaParcelaMapeamento): void {
    this.selecionadas = this.selecionadas.filter(c => c.contaParcelaId !== conta.contaParcelaId);
    this.opcoes = this.marcarSelecionadas(this.opcoes);
  }

  jaSelecionada(conta: ContaParcelaMapeamento): boolean {
    return this.selecionadas.some(c => c.contaParcelaId === conta.contaParcelaId);
  }

  // ---------- substituir ----------

  substituir(): void {
    if (!this.selecionadas.length) {
      return;
    }

    // Com nota ativa, o usuário escolhe se a nota vai para as parcelas selecionadas ou é cancelada.
    if (this.IContaParcelaData.possuiNotaFiscalAtiva) {
      this.dialog.open(ContaParcelaSubstituicaoNotaFiscalComponent, {
        data: {
          tpConta: this.IContaParcelaData.tpConta,
          textoDestino: 'as parcelas selecionadas'
        }
      }).afterClosed().subscribe((acaoNotaFiscal: AcaoNotaFiscalSubstituicao) => {
        if (acaoNotaFiscal) {
          this.substituirPorExistentes(acaoNotaFiscal);
        }
      });

      return;
    }

    const detalhe = this.ehContaAReceber ?
      "Os boletos e/ou notas fiscais associados à parcela atual serão cancelados." :
      "A conta a pagar atual será substituída."

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        titulo: '⚠️ ATENÇÃO',
        mensagem: `Ao substituir esta parcela, a parcela atual será substituída pelas parcelas selecionadas. ${detalhe} Deseja continuar?`
      }
    }).afterClosed().subscribe((confirmou: boolean) => {
      if (!confirmou) {
        return;
      }
      this.substituirPorExistentes(AcaoNotaFiscalSubstituicao.Cancelar);
    });
  }

  private substituirPorExistentes(acaoNotaFiscal: AcaoNotaFiscalSubstituicao): void {
    this.contaParcelaService.substituirPorExistentes(
      this.IContaParcelaData.tpConta,
      this.IContaParcelaData.contaParcelaId,
      this.selecionadas.map(c => c.contaParcelaId),
      acaoNotaFiscal
    ).subscribe(data => {

      const avisos: string[] = data.body?.avisos ?? [];

      if (avisos.length > 0) {
        this.dialog.open(InfoDialogComponent, {
          data: {
            titulo: '⚠️ ATENÇÃO',
            mensagem: 'Conta Parcela substituída, mas: ' + avisos.join('\n')
          }
        });
      } else {
        this.snackBar.open('Conta Parcela substituída com sucesso.', 'OK', { duration: 3000 });
      }

      this.fecharDialog(true); // tela de edição recarrega ao receber true
    });
  }

  private marcarSelecionadas(itens: ContaParcelaMapeamento[]): ContaParcelaMapeamento[] {
    const ids = new Set(this.selecionadas.map(c => c.contaParcelaId));
    return itens.map(i => ({ ...i, jaSelecionada: ids.has(i.contaParcelaId) }));
  }
}